/**
 * The Day-7 treasure chest (539×440 logical frame; the daily tile fixes its width at about
 * 107 px and lets the height follow). A stout wooden chest with gold straps and a gold lock
 * plate, its lid just ajar so gems and a coin peek out of a warm golden gap, and a few
 * sparkles round it. Ink and detail sizes are picked for that 1/5 display scale.
 */
import { TONES } from "../palette.js";
import { celShape, glint, inkStyle, contactShadow } from "../toon-kit.js";
import { TAU, INK, celBands, celBandsY, inkedRope, inkSparkle, roundRectPath, withInkDrop } from "./icon-kit.js";

var L = 13;
var D = 7;
var WOOD = { base: "#c97a3c", light: "#eba868", shade: "#8f4f22" };
var WOOD_LINE = "#6e3a17";
var GOLD = TONES.gold;
var PINK_GEM = { base: "#e24bd8", light: "#ff9cf5", shade: "#a42aa0" };
var BLUE_GEM = { base: "#3fb4ff", light: "#b6e8ff", shade: "#1f72c6" };
var GREEN_GEM = { base: "#3fd08a", light: "#b8f8d6", shade: "#1f9a60" };

var LID_BOTTOM = 206;
var BODY_TOP = 224;

function lidPath(c, g) {
  c.moveTo(48 - g, LID_BOTTOM + g);
  c.lineTo(48 - g, 126);
  c.bezierCurveTo(48 - g, 26 - g, 491 + g, 26 - g, 491 + g, 126);
  c.lineTo(491 + g, LID_BOTTOM + g);
  c.closePath();
}

/** The dome's top edge alone (for the gold rim that runs along it). */
function domeCurve(c) {
  c.moveTo(48, 132);
  c.lineTo(48, 126);
  c.bezierCurveTo(48, 26, 491, 26, 491, 126);
  c.lineTo(491, 132);
}

function bodyPath(c, g) {
  roundRectPath(c, 62 - g, BODY_TOP - g, 415 + g * 2, 182 + g * 2, 20 + g);
}

/** A faceted gem (table on top, point below), centred on (x, y), half-width r. */
function gem(c, x, y, r, tone, rot) {
  c.save();
  c.translate(x, y);
  c.rotate(rot || 0);
  var top = -r * 0.62, girdle = -r * 0.12, bottom = r * 0.95;
  var outline = function (cc, g) {
    cc.moveTo(-r * 0.55 - g * 0.5, top - g);
    cc.lineTo(r * 0.55 + g * 0.5, top - g);
    cc.lineTo(r + g, girdle);
    cc.lineTo(0, bottom + g * 1.3);
    cc.lineTo(-r - g, girdle);
    cc.closePath();
  };
  c.beginPath();
  outline(c, 0);
  c.fillStyle = tone.base;
  c.fill();
  // Facets: lit crown on the left, shaded pavilion on the right.
  c.fillStyle = tone.light;
  c.beginPath();
  c.moveTo(-r * 0.55, top);
  c.lineTo(-r * 0.12, top);
  c.lineTo(-r * 0.32, girdle);
  c.lineTo(-r, girdle);
  c.closePath();
  c.fill();
  c.fillStyle = tone.shade;
  c.beginPath();
  c.moveTo(r * 0.32, girdle);
  c.lineTo(r, girdle);
  c.lineTo(0, bottom);
  c.closePath();
  c.fill();
  c.beginPath();
  c.moveTo(-r, girdle);
  c.lineTo(r, girdle);
  c.moveTo(-r * 0.32, girdle);
  c.lineTo(0, bottom);
  c.lineTo(r * 0.32, girdle);
  inkStyle(c, D * 0.6);
  c.stroke();
  c.beginPath();
  outline(c, 0);
  inkStyle(c, D * 1.15);
  c.stroke();
  glint(c, -r * 0.42, top + r * 0.22, r * 0.16);
  c.restore();
}

/** A gold coin seen a little edge-on. */
function coin(c, x, y, r, rot) {
  c.save();
  c.translate(x, y);
  c.rotate(rot || 0);
  c.beginPath();
  c.ellipse(r * 0.12, r * 0.08, r * 0.78, r, 0, 0, TAU);
  c.fillStyle = GOLD.shade;
  c.fill();
  inkStyle(c, D * 1.1);
  c.stroke();
  c.beginPath();
  c.ellipse(-r * 0.04, 0, r * 0.7, r * 0.9, 0, 0, TAU);
  c.fillStyle = GOLD.base;
  c.fill();
  inkStyle(c, D * 0.7);
  c.stroke();
  c.beginPath();
  c.ellipse(-r * 0.04, 0, r * 0.4, r * 0.56, 0, 0, TAU);
  c.strokeStyle = GOLD.shade;
  c.lineWidth = D * 0.7;
  c.stroke();
  glint(c, -r * 0.34, -r * 0.42, r * 0.16);
  c.restore();
}

/** A gold strap or band: cel bands across its width, ink round it, rivets along it. */
function goldBand(c, x, y, w, h, vertical, rivets) {
  c.beginPath();
  roundRectPath(c, x, y, w, h, Math.min(w, h) * 0.3);
  c.fillStyle = vertical
    ? celBands(c, x, x + w, GOLD, { lit: 0.22, dark: 0.66 })
    : celBandsY(c, y, y + h, GOLD, { lit: 0.34, dark: 0.72 });
  c.fill();
  inkStyle(c, D * 1.25);
  c.stroke();
  if (!rivets) return;
  for (var i = 0; i < rivets.length; i++) {
    var p = rivets[i];
    c.beginPath();
    c.arc(p[0], p[1], 6.5, 0, TAU);
    c.fillStyle = GOLD.light;
    c.fill();
    inkStyle(c, 4);
    c.stroke();
  }
}

function paintChest(c, w, h) {
  contactShadow(c, 272, 406, 222, 16, 0.26);
  // The warm gap between lid and body: treasure glowing inside.
  c.beginPath();
  roundRectPath(c, 66, LID_BOTTOM - 10, 407, BODY_TOP - LID_BOTTOM + 24, 10);
  c.fillStyle = "#5a2a12";
  c.fill();
  c.beginPath();
  c.ellipse(270, BODY_TOP - 2, 190, 13, 0, 0, TAU);
  c.fillStyle = GOLD.light;
  c.fill();

  // Body: boards with a few knots, lit on the left.
  celShape(c, bodyPath, WOOD, 120, {
    line: L,
    lightPath: function (cc) { roundRectPath(cc, 40, BODY_TOP - 30, 58, 230, 20); },
  });
  c.save();
  c.beginPath();
  bodyPath(c, 0);
  c.clip();
  c.beginPath();
  c.moveTo(60, 286); c.lineTo(480, 286);
  c.moveTo(60, 346); c.lineTo(480, 346);
  c.moveTo(186, 286); c.lineTo(186, 346);
  c.moveTo(356, 346); c.lineTo(356, 404);
  c.moveTo(352, 224); c.lineTo(352, 286);
  inkStyle(c, 6, WOOD_LINE);
  c.stroke();
  c.restore();

  // Lid: a taller dome of boards.
  celShape(c, lidPath, WOOD, 120, {
    line: L,
    lightPath: function (cc) { cc.ellipse(150, 86, 120, 36, -0.14, 0, TAU); },
  });
  c.save();
  c.beginPath();
  lidPath(c, 0);
  c.clip();
  c.beginPath();
  c.moveTo(40, 146); c.bezierCurveTo(120, 104, 420, 104, 500, 146);
  c.moveTo(196, 78); c.lineTo(196, LID_BOTTOM);
  c.moveTo(346, 78); c.lineTo(346, LID_BOTTOM);
  inkStyle(c, 6, WOOD_LINE);
  c.stroke();
  c.restore();

  // Gold furniture: corner straps over the dome and down the body, a gold rim round the
  // dome, the lid hem, a foot band and corner caps.
  var straps = [80, 419];
  c.save();
  c.beginPath();
  lidPath(c, L * 0.5);
  c.clip();
  for (var s = 0; s < straps.length; s++) {
    goldBand(c, straps[s], 30, 40, LID_BOTTOM - 30 - 24, true, [[straps[s] + 20, 122], [straps[s] + 20, 160]]);
  }
  c.restore();
  c.beginPath();
  domeCurve(c);
  inkedRope(c, 14, GOLD.base, L * 0.55);
  c.beginPath();
  c.moveTo(70, 104);
  c.bezierCurveTo(90, 74, 150, 58, 210, 52);
  c.strokeStyle = GOLD.light;
  c.lineWidth = 5;
  c.stroke();
  goldBand(c, 56, 360, 427, 32, false);
  for (var b = 0; b < straps.length; b++) {
    goldBand(c, straps[b], BODY_TOP - 4, 40, 182, true, [[straps[b] + 20, 262], [straps[b] + 20, 320]]);
  }
  goldBand(c, 42, LID_BOTTOM - 26, 455, 30, false);

  // Treasure peeking out of the gap.
  gem(c, 134, LID_BOTTOM - 4, 28, GREEN_GEM, -0.6);
  coin(c, 214, LID_BOTTOM - 8, 30, 0.3);
  gem(c, 172, LID_BOTTOM - 22, 44, PINK_GEM, -0.28);
  coin(c, 330, LID_BOTTOM - 4, 26, -0.4);
  gem(c, 380, LID_BOTTOM - 18, 40, BLUE_GEM, 0.34);

  // Lock plate over the seam, with a keyhole.
  var cx = 270;
  celShape(c, function (cc, g) {
    cc.moveTo(cx - 46 - g, 170 - g * 0.2);
    cc.quadraticCurveTo(cx - 46 - g, 156 - g, cx - 30, 156 - g);
    cc.lineTo(cx + 30, 156 - g);
    cc.quadraticCurveTo(cx + 46 + g, 156 - g, cx + 46 + g, 170);
    cc.lineTo(cx + 46 + g, 252);
    cc.quadraticCurveTo(cx + 46 + g, 280 + g, cx, 298 + g);
    cc.quadraticCurveTo(cx - 46 - g, 280 + g, cx - 46 - g, 252);
    cc.closePath();
  }, GOLD, 60, {
    line: L,
    lightPath: function (cc) { cc.ellipse(cx - 32, 190, 18, 46, 0.1, 0, TAU); },
  });
  c.beginPath();
  c.arc(cx, 222, 13, 0, TAU);
  c.moveTo(cx - 6, 228);
  c.lineTo(cx - 10, 262);
  c.lineTo(cx + 10, 262);
  c.lineTo(cx + 6, 228);
  c.closePath();
  c.fillStyle = INK;
  c.fill();
  c.beginPath();
  c.arc(cx, 176, 10, 0, TAU);
  c.fillStyle = BLUE_GEM.base;
  c.fill();
  inkStyle(c, 5);
  c.stroke();
  glint(c, cx - 4, 172, 2.6);
  glint(c, 96, 104, 9);

  // Sparkles round the chest.
  inkSparkle(c, 30, 136, 24, "#ffffff", { line: 5.5 });
  inkSparkle(c, 512, 112, 20, GOLD.light, { line: 5 });
  inkSparkle(c, 516, 300, 14, "#ffffff", { line: 4.5 });
  inkSparkle(c, 24, 300, 12, GOLD.light, { line: 4.5 });
}

/**
 * Day-7 reward: a closed treasure chest, wood and gold, gems peeking from under the lid.
 * Painted 14 units low in its frame: the daily tile lifts the picture 12 px and clips at its
 * own top edge, so the dome's gold rim needs that clearance.
 */
export function drawChest(c, w, h) {
  withInkDrop(c, w, h, 7, function (cc) {
    cc.translate(0, 14);
    paintChest(cc, w, h);
  });
}

